
import DragDrop from '@/components/input/DragDrop.vue'
import Notification from '@/components/helper/Notification.vue'
import GenerateJigFormFields from '@/components/modals/GenerateJigFormFields.vue'
import GenerateJigLoadingSection from '@/components/modals/GenerateJigLoadingSection.vue'
import GenerateJigPreviewSection from '@/components/modals/GenerateJigPreviewSection.vue'
import GenerateJigService, { GenerateJigProgress } from '@/services/GenerateJigService'
import { uploadLibraryModel } from '@/services/generate-jig/upload-library-model'
import { Tenant } from '@/store/modules/app/types'
import { LibraryEntry } from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { Timing } from '@/utils/timing'
import IconBin from '@svg/IconBin.svg'
import IconCheckCircleFilled from '@svg/IconCheckCircleFilled.svg'
import IconCloseFilled from '@svg/IconCloseFilled.svg'
import { Component, Vue } from 'vue-property-decorator'
import { Getter, State } from 'vuex-class'

declare var consoleError: any

type View = 'form' | 'loading' | 'preview'
type SelectedFile = { file: File; name: string; isValid: boolean }
type DragDropFile = File & { isValid?: boolean }

@Component({
  components: {
    DragDrop,
    GenerateJigFormFields,
    GenerateJigLoadingSection,
    GenerateJigPreviewSection,
    IconBin,
    IconCheckCircleFilled,
    IconCloseFilled,
    Notification,
  },
})
export default class GenerateJig extends Vue {
  @Getter('isGlobalAIOptOut', { namespace: Namespace.App })
  public isGlobalAIOptOut!: boolean

  @Getter('allowedModelFileFormats', { namespace: Namespace.App })
  public allowedModelFileFormats!: string[]

  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant

  private view: View = 'form'
  private selectedFile: SelectedFile | null = null
  private isFieldsValid: boolean = false
  private errorMessage: string = ''

  private previewUrl: string = ''
  private generatedJigId: string = ''
  private editUrl: string = ''
  private deeplinkUrl: string = ''
  private previewWarningCopy: string = ''

  private generationRunSequence: number = 0
  private activeGenerationRunId: number = 0
  private activeGenerationService: GenerateJigService | null = null
  private canCancelActiveRun: boolean = false

  private get isGenerateEnabled(): boolean {
    return !!this.selectedFile && this.isFieldsValid && !this.isGlobalAIOptOut
  }

  private beforeDestroy() {
    const service = this.activeGenerationService
    const canCancelActiveRun = this.canCancelActiveRun
    this.clearActiveGenerationRun()

    if (service && canCancelActiveRun) {
      void service.cancel().catch((error: any) => {
        consoleError('Failed to cancel generate jig', error)
      })
    }
  }

  private onFilesSelected(files: DragDropFile[]) {
    if (this.isGlobalAIOptOut) {
      return
    }
    if (!files || files.length === 0) {
      this.selectedFile = null
      return
    }
    const file = files[0]
    if (file.isValid === false) {
      this.selectedFile = null
      return
    }
    this.selectedFile = { file, name: file.name, isValid: true }
    this.errorMessage = ''
  }

  private removeSelectedFile() {
    if (this.isGlobalAIOptOut) {
      return
    }
    this.selectedFile = null
    this.isFieldsValid = false
    this.errorMessage = ''
    const formFields = this.$refs.formFields as InstanceType<typeof GenerateJigFormFields> | undefined
    if (formFields) {
      formFields.reset()
    }
  }

  private onFormValid(isValid: boolean) {
    this.isFieldsValid = isValid
  }

  private isCurrentRun(runId: number, service: GenerateJigService): boolean {
    return this.activeGenerationRunId === runId && this.activeGenerationService === service
  }

  private clearActiveGenerationRun(runId?: number) {
    if (runId != null && runId > 0 && this.activeGenerationRunId !== runId) {
      return
    }
    this.activeGenerationRunId = 0
    this.activeGenerationService = null
    this.canCancelActiveRun = false
  }

  private updateLoadingProgress(progress: GenerateJigProgress) {
    const loadingSection = this.$refs.loadingSection as InstanceType<typeof GenerateJigLoadingSection> | undefined
    if (loadingSection) {
      loadingSection.updateProgress(progress)
    }
  }

  private getDisplayErrorMessage(error: Error | null | undefined): string {
    if (!error || !error.message) {
      return 'Something went wrong. Please try again.'
    }
    const message = error.message
    const exposesInternalPath =
      message.indexOf('/models/') >= 0 ||
      message.indexOf('ItemUrl') >= 0 ||
      message.indexOf('http://') >= 0 ||
      message.indexOf('https://') >= 0
    if (exposesInternalPath) {
      return 'This model could not be prepared for Jig generation. Please check the file and try again.'
    }
    return message
  }

  private resetToForm() {
    this.view = 'form'
    this.previewUrl = ''
    this.generatedJigId = ''
    this.editUrl = ''
    this.deeplinkUrl = ''
    this.previewWarningCopy = ''
    this.errorMessage = ''
    this.selectedFile = null
    this.isFieldsValid = false
  }

  private async onGenerateClick() {
    if (!this.isGenerateEnabled || !this.selectedFile) {
      return
    }

    const formFields = this.$refs.formFields as InstanceType<typeof GenerateJigFormFields> | undefined
    const formPayload = formFields ? formFields.getPayload() : null
    if (!formPayload) {
      return
    }

    const tenantId = this.myTenant && typeof this.myTenant.ID === 'number' ? this.myTenant.ID : -1
    if (tenantId <= 0) {
      this.errorMessage = 'Could not resolve tenant ID for this session.'
      return
    }

    this.errorMessage = ''
    this.view = 'loading'
    await this.$nextTick()
    this.updateLoadingProgress({ message: 'Starting...', percentage: 0 })

    let libraryEntry: LibraryEntry
    try {
      libraryEntry = await uploadLibraryModel({
        store: this.$store,
        file: this.selectedFile.file,
        onProgress: (progress: GenerateJigProgress) => this.updateLoadingProgress(progress),
      })
    } catch (error) {
      const err = error as Error
      this.errorMessage = this.getDisplayErrorMessage(err)
      this.view = 'form'
      return
    }

    const service = new GenerateJigService(tenantId)
    const runId = ++this.generationRunSequence
    this.activeGenerationRunId = runId
    this.activeGenerationService = service
    this.canCancelActiveRun = true

    try {
      const result = await service.generateJig(
        {
          libraryEntry,
          userQuestion: formPayload.description,
          jigName: formPayload.name,
          jigUseCase: formPayload.useCaseLabel,
          jigUseCaseTemplateName: formPayload.useCase,
          libraryModelReferences: [],
          tenantName: this.myTenant && this.myTenant.Name ? this.myTenant.Name : '',
        },
        (progress: GenerateJigProgress) => {
          if (!this.isCurrentRun(runId, service)) {
            return
          }
          // GenerateJigService reports 0–100 for its phase. Remap to 60–100 so the
          // bar grows monotonically across upload (0–60) and generation (60–100).
          const remapped: GenerateJigProgress = {
            message: progress.message,
            percentage: 60 + Math.round((progress.percentage || 0) * 0.4),
          }
          this.updateLoadingProgress(remapped)
        }
      )

      if (!this.isCurrentRun(runId, service)) {
        return
      }

      service.commitGeneratedJig()
      this.canCancelActiveRun = false

      this.updateLoadingProgress({ message: 'Jig created successfully.', percentage: 100 })
      await Timing.sleep(260)

      if (!this.isCurrentRun(runId, service)) {
        return
      }

      this.generatedJigId = result.jigId
      this.previewUrl = result.previewURL
      this.editUrl = result.editURL
      this.deeplinkUrl = result.deeplinkURL
      this.previewWarningCopy = result.warningCopy || ''

      this.view = 'preview'
      this.clearActiveGenerationRun(runId)
    } catch (error) {
      if (!this.isCurrentRun(runId, service)) {
        return
      }
      this.clearActiveGenerationRun(runId)
      const err = error as Error
      this.errorMessage = this.getDisplayErrorMessage(err)
      this.view = 'form'
    }
  }
}
